import {createSlice, type PayloadAction} from '@reduxjs/toolkit'
import type {TRootState} from '..'
import type {Applicant, ApplicantsResponse} from './../../models/applicant'
import type {TFlag} from './../../models/candidateFilters'
import {applicantsApi} from './../apis/applicants-api'

export interface IApplicantsState {
  applicant: Applicant | undefined
  // The candidate list the hosts render (CandidatesContainer, Candidates). Written only
  // by useCandidatesData.fetchCandidates, with the answer of its newest list request, so
  // it always matches the hook's own list, total and selection, and a stale answer (an
  // unfiltered prefill fetch resolving after the filtered one, a superseded load) can't
  // clobber it.
  allApplicants: ApplicantsResponse | undefined
}
const initialState: IApplicantsState = {
  applicant: undefined,
  allApplicants: undefined,
}

const applicantSlice = createSlice({
  name: 'applicantsSlice',
  initialState,
  reducers: {
    // Clear all applicants state - used when switching agencies to prevent stale data
    clearApplicantsState: (state) => {
      state.applicant = undefined
      state.allApplicants = undefined
    },
    // Explicitly set the applicant - more reliable than matcher after resetApiState()
    setApplicant: (state, action: PayloadAction<Applicant | undefined>) => {
      state.applicant = action.payload
    },
    // STFFLG-4244: the answer useCandidatesData accepted for its newest list request. This
    // used to be a getApplicants matcher keeping the most recently STARTED request's answer,
    // but a call whose args match a request still in flight starts no request (RTK folds it
    // into the running one, and swallows its condition rejection before any reducer), so the
    // slice could keep one answer while the hook kept another: rows from one request listed
    // under the other's gate, total and selection.
    setAllApplicants: (state, action: PayloadAction<ApplicantsResponse | undefined>) => {
      state.allApplicants = action.payload
    },
    // Optimistically reflect a flag change in the cached list + open profile. The
    // getApplicants LIST endpoint is search-indexed and lags the write, so re-fetching
    // it would return the stale flag until the index catches up — patching the cache
    // directly updates the list chip, the focused card, and the flag menu immediately.
    patchApplicantFlag: (state, action: PayloadAction<{applicantId: string; flag: TFlag}>) => {
      const {applicantId, flag} = action.payload
      if (state.applicant?.id === applicantId) {
        state.applicant.flag = flag
      }
      const listItem = state.allApplicants?.applicants?.find((a) => a.id === applicantId)
      if (listItem) {
        listItem.flag = flag
      }
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(applicantsApi.endpoints.getApplicant.matchFulfilled, (state, {payload}) => {
      state.applicant = payload
    })
  },
})

export const {clearApplicantsState, setApplicant, setAllApplicants, patchApplicantFlag} =
  applicantSlice.actions
export const selectApplicantsState = (state: TRootState): TRootState['applicants'] =>
  state.applicants
export default applicantSlice.reducer
